import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import {
  Box,
  Heading,
  Text,
  useColorModeValue,
  SimpleGrid,
} from '@chakra-ui/react';
import CalcButton from './components/CalcButton';
import {
  calculatorReducer,
  getExpressionLine,
  getMainDisplay,
  initialCalculatorState,
} from './calculatorEngine';

const Calculator = () => {
  const subColor = useColorModeValue('gray.500', 'gray.400');
  const panelBg = useColorModeValue('white', 'gray.800');
  const panelBorder = useColorModeValue('gray.200', 'gray.600');
  const displayBg = useColorModeValue('gray.50', 'gray.900');
  const displayColor = useColorModeValue('gray.800', 'gray.100');
  const exprColor = useColorModeValue('gray.500', 'gray.400');

  const [state, dispatch] = useReducer(calculatorReducer, initialCalculatorState);

  const expressionLine = useMemo(() => getExpressionLine(state), [state]);
  const mainDisplay = useMemo(() => getMainDisplay(state), [state]);

  const dispatchOp = useCallback((type, payload) => {
    dispatch({ type, ...payload });
  }, []);

  const mainLen = mainDisplay.length;
  const mainFontSize =
    mainLen > 14 ? { base: 'xl', sm: '2xl' } : mainLen > 10 ? { base: '2xl', sm: '3xl' } : { base: '3xl', sm: '4xl' };

  const exprFontSize = expressionLine.length > 28 ? 'xs' : expressionLine.length > 20 ? 'sm' : 'md';

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.defaultPrevented) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }

      const key = e.key;

      if (key >= '0' && key <= '9') {
        e.preventDefault();
        dispatchOp('DIGIT', { digit: key });
        return;
      }

      if (key === '.') {
        e.preventDefault();
        dispatch({ type: 'DECIMAL' });
        return;
      }

      if (key === '+' || key === '-') {
        e.preventDefault();
        dispatchOp('OPERATOR', { op: key });
        return;
      }

      if (key === '*') {
        e.preventDefault();
        dispatchOp('OPERATOR', { op: '*' });
        return;
      }

      if (key === '/') {
        e.preventDefault();
        e.stopPropagation();
        dispatchOp('OPERATOR', { op: '/' });
        return;
      }

      if (key === '%') {
        e.preventDefault();
        dispatch({ type: 'PERCENT' });
        return;
      }

      if (key === 'Enter' || key === '=') {
        e.preventDefault();
        dispatch({ type: 'EQUALS' });
        return;
      }

      if (key === 'Escape') {
        e.preventDefault();
        dispatch({ type: 'CLEAR' });
        return;
      }

      if (key === 'Backspace') {
        e.preventDefault();
        dispatch({ type: 'BACKSPACE' });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dispatchOp]);

  const liveSummary =
    expressionLine && expressionLine.length > 0
      ? `${expressionLine}, ${mainDisplay}`
      : mainDisplay;

  return (
    <Box maxW="420px" mx="auto">
      <Box mb={5}>
        <Heading as="h1" size="md" fontWeight="600" mb={0.5}>
          Calculator
        </Heading>
        <Text fontSize="sm" color={subColor}>
          Expression above, result below — keys: 0–9, ., + − * /, %, Enter, Esc, Backspace
        </Text>
      </Box>

      <Box
        role="application"
        aria-label="Calculator"
        tabIndex={-1}
        bg={panelBg}
        borderWidth="1px"
        borderColor={panelBorder}
        borderRadius="2xl"
        p={{ base: 4, sm: 5 }}
        shadow="sm"
      >
        <Box
          bg={displayBg}
          borderRadius="xl"
          px={4}
          py={4}
          mb={4}
          minH={{ base: '100px', sm: '108px' }}
          display="flex"
          flexDirection="column"
          alignItems="stretch"
          justifyContent="flex-end"
          gap={1}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          aria-label={liveSummary}
        >
          <Box
            w="100%"
            maxW="100%"
            minH="1.35em"
            overflowX="auto"
            overflowY="hidden"
            sx={{
              direction: 'rtl',
              scrollbarWidth: 'thin',
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': { height: '4px' },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 'full',
                bg: 'blackAlpha.200',
              },
            }}
            title={expressionLine || undefined}
          >
            <Text
              as="div"
              fontSize={exprFontSize}
              fontWeight="500"
              color={exprColor}
              lineHeight="1.25"
              textAlign="right"
              fontVariantNumeric="tabular-nums"
              whiteSpace="nowrap"
              sx={{
                direction: 'ltr',
                display: 'inline-block',
                minWidth: '100%',
              }}
            >
              {expressionLine || '\u00a0'}
            </Text>
          </Box>
          <Text
            as="div"
            fontSize={mainFontSize}
            fontWeight="600"
            color={mainDisplay === 'Error' ? 'red.500' : displayColor}
            lineHeight="1.1"
            maxW="100%"
            textAlign="right"
            fontVariantNumeric="tabular-nums"
            letterSpacing="-0.02em"
            noOfLines={1}
            overflow="hidden"
            textOverflow="ellipsis"
            title={mainDisplay}
          >
            {mainDisplay}
          </Text>
        </Box>

        <SimpleGrid columns={4} spacing={{ base: 2, sm: 2.5 }} templateRows="repeat(5, minmax(48px, 1fr))">
          <CalcButton
            label="AC"
            variant="action"
            ariaLabel="All clear"
            onPress={() => dispatch({ type: 'CLEAR' })}
          />
          <CalcButton
            label="⌫"
            variant="action"
            ariaLabel="Backspace"
            onPress={() => dispatch({ type: 'BACKSPACE' })}
          />
          <CalcButton
            label="%"
            variant="action"
            ariaLabel="Percent"
            onPress={() => dispatch({ type: 'PERCENT' })}
          />
          <CalcButton
            label="÷"
            variant="operator"
            ariaLabel="Divide"
            onPress={() => dispatchOp('OPERATOR', { op: '/' })}
          />

          {['7', '8', '9'].map((d) => (
            <CalcButton key={d} label={d} onPress={() => dispatchOp('DIGIT', { digit: d })} />
          ))}
          <CalcButton
            label="×"
            variant="operator"
            ariaLabel="Multiply"
            onPress={() => dispatchOp('OPERATOR', { op: '*' })}
          />

          {['4', '5', '6'].map((d) => (
            <CalcButton key={d} label={d} onPress={() => dispatchOp('DIGIT', { digit: d })} />
          ))}
          <CalcButton
            label="−"
            variant="operator"
            ariaLabel="Subtract"
            onPress={() => dispatchOp('OPERATOR', { op: '-' })}
          />

          {['1', '2', '3'].map((d) => (
            <CalcButton key={d} label={d} onPress={() => dispatchOp('DIGIT', { digit: d })} />
          ))}
          <CalcButton
            label="+"
            variant="operator"
            ariaLabel="Add"
            onPress={() => dispatchOp('OPERATOR', { op: '+' })}
          />

          <CalcButton
            label="±"
            variant="action"
            ariaLabel="Toggle sign"
            onPress={() => dispatch({ type: 'SIGN' })}
          />
          <CalcButton label="0" onPress={() => dispatchOp('DIGIT', { digit: '0' })} />
          <CalcButton
            label="."
            variant="digit"
            ariaLabel="Decimal point"
            onPress={() => dispatch({ type: 'DECIMAL' })}
          />
          <CalcButton
            label="="
            variant="equals"
            ariaLabel="Equals"
            onPress={() => dispatch({ type: 'EQUALS' })}
          />
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default Calculator;
